import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Button, Table, Tag, Select } from "antd";
import Layout from "../../Layout";
import CustomerDetailDrawer from "./components/CustomerDetailDrawer";

interface CustomerItem {
    key: string;
    code: string;
    name: string;
    phone: string;
    serviceCategory: "Newhair" | "Clinic" | "PTTM" | "Partnership";
    csCode: "CS0" | "CS1" | "CS2" | "CS3" | "CS4" | "CS5" | "CS6";
    flags: string[];
    latestService: string;
    date: string;
    remainingSessions: string;
    nextMilestone: string;
}

export default function KhachHangCSKHPage() {
    const { t } = useTranslation();

    // State bộ lọc (Filter)
    const [selectedCS, setSelectedCS] = useState<string>("all");
    const [selectedFlag, setSelectedFlag] = useState<string>("all");
    const [selectedCategory, setSelectedCategory] = useState<string>("all");

    // State quản lý drawer chi tiết khách hàng bên phải
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerItem | null>(null);

    // State trạng thái nhân viên CSKH góc dưới
    const [staffStatus, setStaffStatus] = useState<"available" | "break" | "offline">("available");

    // Dữ liệu mẫu danh sách khách hàng
    const customersData: CustomerItem[] = [
        {
            key: "1",
            code: "KH-19950",
            name: "Chị L.B.T",
            phone: "+61 4*** *** 118",
            serviceCategory: "Clinic",
            csCode: "CS4",
            flags: ["Ở nước ngoài — Sydney (+4 giờ)"],
            latestService: "Phác đồ K-Reskin · Basic · buổi trải nghiệm",
            date: "20/12/2025",
            remainingSessions: "—",
            nextMilestone: "T+30 gợi ý buổi tiếp · 19/01/2026",
        },
        {
            key: "2",
            code: "KH-16002",
            name: "Chị N.H.A",
            phone: "093***5521",
            serviceCategory: "Clinic",
            csCode: "CS2",
            flags: ["Biến chứng", "Đang theo dõi sau dịch vụ"],
            latestService: "Phác đồ K-Lift · Basic",
            date: "13/10",
            remainingSessions: "2 buổi · TL-1001",
            nextMilestone: "T+3 · 16/10",
        },
        {
            key: "3",
            code: "KH-15890",
            name: "Anh P.Q.D",
            phone: "090***8813",
            serviceCategory: "Newhair",
            csCode: "CS2",
            flags: ["Khiếu nại đang mở", "Còn nợ"],
            latestService: "Tái khám sau cấy",
            date: "13/10",
            remainingSessions: "5 buổi · AF-0915",
            nextMilestone: "T+90 · 14/12",
        },
        {
            key: "4",
            code: "KH-17311",
            name: "Chị H.T.M",
            phone: "090***0928",
            serviceCategory: "Clinic",
            csCode: "CS2",
            flags: ["Đang theo dõi sau dịch vụ"],
            latestService: "Phác đồ K-Lift · Advanced",
            date: "14/10",
            remainingSessions: "2 buổi · TL-1002",
            nextMilestone: "T+24h hỏi thăm · 15/10",
        },
        {
            key: "5",
            code: "KH-14421",
            name: "Anh L.V.T",
            phone: "098***2204",
            serviceCategory: "Newhair",
            csCode: "CS2",
            flags: ["Đang theo dõi sau dịch vụ"],
            latestService: "Giải pháp Newhair · Layer-Shaving · mức độ 3",
            date: "14/10",
            remainingSessions: "12 buổi · AF-1014",
            nextMilestone: "T+24h hỏi thăm · 15/10",
        },
        {
            key: "6",
            code: "KH-18050",
            name: "Chị B.T.H",
            phone: "093***4470",
            serviceCategory: "PTTM",
            csCode: "CS1",
            flags: ["Đang theo dõi sau dịch vụ", "Còn nợ"],
            latestService: "Gói phẫu thuật theo độ tuổi 25–45 · Face · Transformation (mắt)",
            date: "10/10",
            remainingSessions: "—",
            nextMilestone: "T+7 tái khám, cắt chỉ · 17/10",
        },
        {
            key: "7",
            code: "KH-12087",
            name: "Chị Đ.T.L",
            phone: "091***4436",
            serviceCategory: "Clinic",
            csCode: "CS2",
            flags: ["Khách VIP", "Thẻ sắp hết hạn", "Khiếu nại đang mở"],
            latestService: "Phác đồ K-Renewal · Basic",
            date: "28/07",
            remainingSessions: "2 buổi · TL-0310",
            nextMilestone: "T+30 buổi 4/5 · 27/08",
        },
    ];

    // Logic lọc dữ liệu dựa trên các nút filter ở phía trên
    const filteredCustomers = customersData.filter((item) => {
        // Lọc theo CS code
        if (selectedCS !== "all" && item.csCode !== selectedCS) return false;
        // Lọc theo Mảng dịch vụ
        if (selectedCategory !== "all" && item.serviceCategory !== selectedCategory) return false;
        // Lọc theo Cờ
        if (selectedFlag !== "all") {
            if (!item.flags.some((f) => f.toLowerCase().includes(selectedFlag.toLowerCase()))) return false;
        }
        return true;
    });

    const columns = [
        {
            title: "Khách",
            key: "customer",
            render: (_: any, record: CustomerItem) => (
                <div className="space-y-0.5">
                    <div className="flex items-center gap-1.5 font-medium">
                        <span
                            onClick={() => {
                                setSelectedCustomer(record);
                                // Nếu drawer đang bị thu nhỏ dưới góc, khi click chọn khách mới sẽ tự bật sáng lên lại
                                const drawerElement = document.getElementById("customer-detail-drawer");
                                // Hoặc quản lý bằng state mở rộng nếu cần
                            }}
                            className="font-bold text-[#1d4c7d] dark:text-blue-400 cursor-pointer hover:underline"
                        >
                            {record.code} · {record.name}
                        </span>
                    </div>
                    <div className="text-xs text-gray-500">{record.phone}</div>
                </div>
            ),
        },
        {
            title: "Mảng",
            dataIndex: "serviceCategory",
            key: "serviceCategory",
            width: 100,
            render: (text: string) => <span className="text-gray-700 dark:text-gray-300 font-medium">{text}</span>,
        },
        {
            title: "Mã CS",
            dataIndex: "csCode",
            key: "csCode",
            width: 80,
            render: (text: string) => <Tag className="bg-blue-600 text-white font-bold border-0 m-0">{text}</Tag>,
        },
        {
            title: "Cờ",
            key: "flags",
            render: (_: any, record: CustomerItem) => (
                <div className="flex flex-wrap gap-1">
                    {record.flags.map((flag, idx) => {
                        let color = "default";
                        if (flag.includes("Biến chứng")) color = "red";
                        else if (flag.includes("Khiếu nại")) color = "red";
                        else if (flag.includes("Còn nợ")) color = "orange";
                        else if (flag.includes("VIP")) color = "purple";
                        else if (flag.includes("Thẻ sắp hết hạn")) color = "volcano";
                        else if (flag.includes("nước ngoài")) color = "blue";
                        return (
                            <Tag key={idx} color={color} className="m-0 text-xs px-1.5 py-0.5">
                                {flag}
                            </Tag>
                        );
                    })}
                </div>
            ),
        },
        {
            title: "Dịch vụ gần nhất",
            dataIndex: "latestService",
            key: "latestService",
            render: (text: string) => <span className="text-gray-800 dark:text-gray-200">{text}</span>,
        },
        {
            title: "Ngày",
            dataIndex: "date",
            key: "date",
            width: 90,
            render: (text: string) => <span className="text-gray-700 dark:text-gray-300">{text}</span>,
        },
        {
            title: "Thẻ còn buổi",
            dataIndex: "remainingSessions",
            key: "remainingSessions",
            width: 120,
            render: (text: string) => <span className="text-gray-700 dark:text-gray-300">{text}</span>,
        },
        {
            title: "Mốc kế tiếp",
            dataIndex: "nextMilestone",
            key: "nextMilestone",
            render: (text: string) => <span className="text-gray-700 dark:text-gray-300 font-medium">{text}</span>,
        },
        {
            title: "Thao tác",
            key: "actions",
            width: 160,
            render: (_: any, record: CustomerItem) => (
                <div className="flex items-center gap-1.5">
                    <Button size="small" type="primary" className="!bg-[#3E91AB] text-xs h-7 px-2.5">
                        Gọi
                    </Button>
                    <Button size="small" className="text-xs h-7 px-2.5 border-gray-300 dark:border-gray-600">
                        Zalo
                    </Button>
                </div>
            ),
        },
    ];

    const getStatusDotColor = () => {
        switch (staffStatus) {
            case "available":
                return "#52c41a";
            case "break":
                return "#d48806";
            case "offline":
                return "#8c8c8c";
            default:
                return "#52c41a";
        }
    };

    return (
        <Layout>
            <Helmet>
                <title>Khách hàng (CS0–CS6) | Khothi</title>
            </Helmet>

            <div className="min-h-screen text-sm flex flex-col justify-between transition-colors duration-200">
                <div className="flex flex-1 overflow-hidden gap-4">
                    {/* Phần nội dung chính bên trái */}
                    <div className="space-y-4 overflow-y-auto pb-16 w-full">
                        
                        {/* Header Tiêu đề & Quy tắc */}
                        <div className="flex flex-wrap items-center justify-between border-b border-gray-300 dark:border-gray-700 pb-2 gap-2">
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 border-l-4 border-[#1d4c7d] pl-2">
                                <span className="font-bold text-[#1d4c7d] dark:text-blue-400 text-base flex items-center gap-2">
                                    Khách hàng (CS0–CS6) <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded">mới</span>
                                </span>
                                <span className="text-gray-600 dark:text-gray-400">
                                    Vai trò: CSKH, Trưởng phòng CSKH <span className="text-[#3E91AB] cursor-pointer">[?]</span>
                                </span>
                                <span className="text-gray-600 dark:text-gray-400 hidden xl:inline">
                                    Quy tắc: R34 chuyển giao sau thanh toán lần đầu · R35 gán mã CS · R37 cờ song song với trạng thái
                                </span>
                            </div>
                        </div>

                        {/* Dòng Khung Cam DỮ LIỆU MINH HOẠ */}
                        <div className="border border-dashed border-orange-400 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 text-center py-1.5 font-bold tracking-wider rounded text-xs">
                            DỮ LIỆU MINH HOẠ — tên khách, mã nhân viên và mọi con số là giả
                        </div>

                        {/* Thống kê đếm số lượng & Lọc theo Mã CS (Tối ưu chuẩn Responsive cho iPad và Mobile) */}
                        <div className="flex flex-wrap gap-2">
                            <div
                                onClick={() => setSelectedCS("all")}
                                className={`p-2 rounded border cursor-pointer transition-all ${
                                    selectedCS === "all"
                                        ? "border-[#1d4c7d] bg-blue-50 dark:bg-blue-950/40 shadow-sm"
                                        : "bg-white dark:bg-[#1f1f1f] border-gray-200 dark:border-gray-800 hover:border-gray-300"
                                }`}
                            >
                                <div className="text-lg font-bold text-gray-900 dark:text-gray-100">923</div>
                                <div className="text-xs text-gray-500 font-medium">Tất cả</div>
                            </div>

                            {[
                                { code: "CS0", label: "Mới chuyển sang, chờ T+24h", count: 6 },
                                { code: "CS1", label: "Đang chăm sóc sau dịch vụ", count: 18 },
                                { code: "CS2", label: "Đang trong liệu trình", count: 142 },
                                { code: "CS3", label: "Hoàn tất liệu trình, ổn định", count: 260 },
                                { code: "CS4", label: "Đã đặt lịch dùng tiếp / mua thêm", count: 35 },
                                { code: "CS5", label: "Lâu chưa quay lại", count: 410 },
                                { code: "CS6", label: "Ngừng chăm sóc", count: 52 },
                            ].map((item) => (
                                <div
                                    key={item.code}
                                    onClick={() => setSelectedCS(item.code)}
                                    className={`p-2 rounded border cursor-pointer transition-all flex flex-col justify-between ${
                                        selectedCS === item.code
                                            ? "border-[#1d4c7d] bg-blue-50 dark:bg-blue-950/40 shadow-sm"
                                            : "bg-white dark:bg-[#1f1f1f] border-gray-200 dark:border-gray-800 hover:border-gray-300"
                                    }`}
                                >
                                    <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{item.count}</div>
                                    {/* Đã gỡ bỏ chữ truncate và cho phép text xuống dòng linh hoạt, dễ đọc trên iPad/Mobile */}
                                    <div className="text-[11px] text-gray-500 leading-tight mt-1 whitespace-normal">
                                        <span className="font-semibold text-gray-700 dark:text-gray-300">{item.code}</span> · {item.label}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Bộ lọc theo Cờ (Flags) */}
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="font-bold text-gray-700 dark:text-gray-300">Cờ:</span>
                            {[
                                { key: "all", label: "Tất cả" },
                                { key: "Biến chứng", label: "Biến chứng" },
                                { key: "Khiếu nại", label: "Khiếu nại" },
                                { key: "Thẻ sắp hết hạn", label: "Thẻ sắp hết hạn" },
                                { key: "VIP", label: "VIP" },
                                { key: "Không gọi", label: "Không gọi" },
                            ].map((flag) => (
                                <Button
                                    key={flag.key}
                                    size="small"
                                    type={selectedFlag === flag.key ? "primary" : "default"}
                                    className={selectedFlag === flag.key ? "!bg-[#1d4c7d]" : ""}
                                    onClick={() => setSelectedFlag(flag.key)}
                                >
                                    {flag.label}
                                </Button>
                            ))}
                        </div>

                        {/* Bộ lọc theo Mảng dịch vụ */}
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                            <span className="font-bold text-gray-700 dark:text-gray-300">Mảng:</span>
                            {[
                                { key: "all", label: "Tất cả" },
                                { key: "Newhair", label: "Newhair" },
                                { key: "Clinic", label: "Clinic" },
                                { key: "PTTM", label: "PTTM" },
                                { key: "Partnership", label: "Partnership" },
                            ].map((cat) => (
                                <Button
                                    key={cat.key}
                                    size="small"
                                    type={selectedCategory === cat.key ? "primary" : "default"}
                                    className={selectedCategory === cat.key ? "!bg-[#1d4c7d]" : ""}
                                    onClick={() => setSelectedCategory(cat.key)}
                                >
                                    {cat.label}
                                </Button>
                            ))}
                        </div>

                        {/* Bảng Danh Sách Khách Hàng */}
                        <div className="bg-white dark:bg-[#1f1f1f] rounded border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
                            <Table
                                columns={columns}
                                dataSource={filteredCustomers}
                                rowKey="key"
                                pagination={{ pageSize: 5 }}
                                size="middle"
                                className="custom-waiting-table"
                                scroll={{ x: "max-content" }}
                            />
                        </div>

                        <div className="text-xs text-gray-500 dark:text-gray-400 px-1 italic">
                            Danh sách CSKH = khách đã thanh toán dịch vụ ít nhất 1 lần. Cùng một hồ sơ khách với Telesales; chỉ khác danh sách và bộ trạng thái.
                        </div>
                    </div>

                    {/* Phần Drawer Chi Tiết Khách Hàng Bên Phải (Tách thành Component riêng biệt) */}
                    {selectedCustomer && (
                        <CustomerDetailDrawer
                            customer={selectedCustomer}
                            onClose={() => setSelectedCustomer(null)}
                        />
                    )}
                </div>

                {/* Floating Widget Thanh Trạng Thái (PC 1 hàng, Mobile tự động xuống 2 hàng) */}
                <div className="sticky bottom-3 z-27 sm:mx-0 bg-[#1d4c7d] dark:bg-[#142f4c] text-white rounded-lg shadow-xl px-3.5 py-2.5 flex flex-col sm:flex-row items-center justify-between sm:justify-start gap-2 sm:gap-3 w-full sm:w-fit border border-[#3E91AB]">
                    
                    {/* Phần thông tin nhân viên và Select trạng thái */}
                    <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-2">
                        <div className="flex items-center gap-2">
                            <span
                                className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                                style={{ backgroundColor: getStatusDotColor() }}
                            ></span>
                            <span className="font-bold text-sm whitespace-nowrap">
                                NV-07 · CSKH
                            </span>
                        </div>

                        {/* Trên mobile hiển thị Select ở đây cho cân đối dòng 1 */}
                        <div className="block sm:hidden">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[110px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>
                    </div>

                    {/* Phần Select (cho PC) và các nút bấm hành động */}
                    <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto border-t border-blue-400/30 pt-2 sm:border-t-0 sm:pt-0">
                        
                        {/* Trên PC hiển thị Select ở vị trí này */}
                        <div className="hidden sm:block">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[125px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>

                        <span className="text-xs text-gray-200 hidden md:inline whitespace-nowrap">
                            Không có cuộc gọi
                        </span>

                        <div className="flex items-center gap-1.5 w-full sm:w-auto justify-end">
                            <Button
                                size="small"
                                className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial"
                            >
                                Mô phỏng gọi đến
                            </Button>

                            <Button
                                size="small"
                                className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial"
                            >
                                Gọi số mới
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}